import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useRestApi } from 'context/RestApiContext';
import { IconButton, Badge, Menu, MenuItem } from '@mui/material';
import TaskCard from 'views/TaskCards/TaskCard';
import TaskCardEditModal from 'views/TaskCards/TaskCardEditModal';
import TaskCardAddModal from 'views/TaskCards/TaskCardAddModal';
import TaskArchiveSearchModal from 'views/TaskCards/TaskArchiveSearchModal';
import RecurringTaskManageModal from 'views/TaskCards/RecurringTaskManageModal';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import SearchIcon from '@mui/icons-material/Search';
import { MessageModal } from 'components/modal/MessageModal';
import './TaskCards.css';
import TaskNewNotificationButtonIcon from 'views/TaskCards/TaskNewNotificationButtonIcon';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { formatDateTime } from 'utils/dateUtils';

const TaskCards = () => {
  const restApi = useRestApi();
  const [columns, setColumns] = useState([]);

  // 確認ダイアログ
  const [dialog, setDialog] = useState({});

  // 新着コメント
  const [newFlg, setNewFlg] = useState(false);

  // リスト追加用の状態
  const [isAddingList, setIsAddingList] = useState(false);
  const [newListName, setNewListName] = useState('');

  // リスト名のインライン編集状態
  const [editingListId, setEditingListId] = useState(null);
  const [editingListName, setEditingListName] = useState('');

  // カード編集モーダルのパラメタ
  const [editModalparam, setEditModalparam] = useState({});
  // カード編集モーダルの呼び出し処理
  const callEditModal = (card) => {
    setEditModalparam({
      card: card,
    });
  };

  // タスクカード追加モーダルのパラメタ
  const [taskCardAddModalparam, setTaskCardAddModalparam] = useState({});
  // タスクカード追加モーダルの呼び出し処理
  const callAddModal = (listId) => {
    setTaskCardAddModalparam({
      show: true,
      mode: 'add',
      LIST_ID: listId,
      callBack: () => {
        loadCards();
      },
    });
  };

  // カード検索のパラメタ
  const [taskArchiveSearchModalparam, setTaskArchiveSearchModalparam] = useState({});
  const handleOnClickCardSearch = () => {
    setTaskArchiveSearchModalparam({
      show: true,
    });
  };

  // 定期タスク管理モーダルのパラメタ
  const [recurringManageParam, setRecurringManageParam] = useState({});
  const handleOnClickRecurringManage = () => {
    setRecurringManageParam({ show: true });
  };

  // 初期表示
  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = () => {
    // カード一覧を取得
    restApi.get('/api/task/cards', (data) => {
      setColumns(data.card_list);
      setNewFlg(data.new_flg);
    });
  };

  // タスクの完了状態の更新
  const updateCompleteCard = (card) => {
    // 親のcolumns stateも更新して、ドラッグ操作時に古い値が使われないようにする
    setColumns((prev) =>
      prev.map((col) => ({
        ...col,
        CARDS: col.CARDS.map((c) =>
          c.CARD_ID === card.CARD_ID ? { ...c, COMPLETE_FLG: card.COMPLETE_FLG } : c
        ),
      }))
    );
    restApi.post(
      `/api/task/cards/${card.CARD_ID}`,
      { COMPLETE_FLG: card.COMPLETE_FLG },
      (data) => {}
    );
  };

  const onDragEnd = (result) => {
    const { source, destination, type } = result;

    if (!destination) return;

    if (type === 'COLUMN') {
      const newColumns = Array.from(columns);
      const [moved] = newColumns.splice(source.index, 1);
      newColumns.splice(destination.index, 0, moved);
      setColumns(newColumns);
      restApi.post(
        '/api/task/lists/reorder',
        { order: newColumns.map((c) => c.LIST_ID) },
        (_) => {}
      );
      return;
    }

    const sourceListId = source.droppableId;
    const destinationListId = destination.droppableId;

    if (sourceListId === destinationListId && destination.index === source.index) return;

    const sourceColumnIndex = columns.findIndex((c) => String(c.LIST_ID) === sourceListId);
    const destinationColumnIndex = columns.findIndex(
      (c) => String(c.LIST_ID) === destinationListId
    );
    if (sourceColumnIndex === -1 || destinationColumnIndex === -1) return;

    const sourceColumn = columns[sourceColumnIndex];
    const destinationColumn = columns[destinationColumnIndex];

    const sourceCards = Array.from(sourceColumn.CARDS);
    const [movedCard] = sourceCards.splice(source.index, 1);
    const destinationCards =
      sourceColumnIndex === destinationColumnIndex
        ? sourceCards
        : Array.from(destinationColumn.CARDS);
    destinationCards.splice(destination.index, 0, movedCard);

    const newColumns = Array.from(columns);
    newColumns[sourceColumnIndex] = { ...sourceColumn, CARDS: sourceCards };
    newColumns[destinationColumnIndex] = { ...destinationColumn, CARDS: destinationCards };

    restApi.post(
      '/api/task/cards/move',
      {
        sourceListId: Number(sourceListId),
        sourceList: newColumns[sourceColumnIndex].CARDS,
        destinationListId: Number(destinationListId),
        destinationList: newColumns[destinationColumnIndex].CARDS,
      },
      (_) => {}
    );

    setColumns(newColumns);
  };

  // リスト追加のハンドラ
  const startAddList = () => setIsAddingList(true);
  const cancelAddList = () => {
    setIsAddingList(false);
    setNewListName('');
  };
  const confirmAddList = () => {
    const name = newListName.trim();
    if (!name) return;
    restApi.post('/api/task/lists/add', { LIST_NAME: name, POSITION: columns.length }, (_) => {
      cancelAddList();
      loadCards();
    });
  };

  // リスト名編集ハンドラ
  const startEditListName = (column) => {
    setEditingListId(column.LIST_ID);
    setEditingListName(column.LIST_NAME ?? '');
  };
  const cancelEditListName = () => {
    setEditingListId(null);
    setEditingListName('');
  };
  const saveEditListName = (listId) => {
    const name = editingListName.trim();
    if (!name) {
      cancelEditListName();
      return;
    }
    // 変更がない場合は何もしない
    const target = columns.find((c) => c.LIST_ID === listId);
    if (target && target.LIST_NAME === name) {
      cancelEditListName();
      return;
    }

    setColumns((prev) => prev.map((c) => (c.LIST_ID === listId ? { ...c, LIST_NAME: name } : c)));
    cancelEditListName();

    restApi.post(`/api/task/lists/${listId}`, { LIST_NAME: name }, (_) => {});
  };

  // リストメニューの状態
  const [listMenuAnchorEl, setListMenuAnchorEl] = useState(null);
  const [listMenuListId, setListMenuListId] = useState(null);
  const isListMenuOpen = Boolean(listMenuAnchorEl);

  // リストメニューの開閉
  const openListMenu = (event, listId) => {
    setListMenuAnchorEl(event.currentTarget);
    setListMenuListId(listId);
  };
  const closeListMenu = () => {
    setListMenuAnchorEl(null);
    setListMenuListId(null);
  };

  // メニューアクション
  const handleMenuAddCard = () => {
    if (listMenuListId != null) {
      callAddModal(listMenuListId);
    }
    closeListMenu();
  };
  const handleMenuArchiveList = () => {
    const id = listMenuListId;
    const listName = columns.find((c) => c.LIST_ID === id)?.LIST_NAME || '';
    closeListMenu();
    if (id == null) return;
    setDialog({
      type: 'confirm',
      message: `リスト「${listName}」をアーカイブします。よろしいですか？`,
      okCallBack: () => {
        restApi.post(
          `/api/task/lists/${id}`,
          { ARCHIVE_FLG: 1, ARCHIVE_DATE: formatDateTime(new Date()) },
          (_) => {
            loadCards();
          }
        );
      },
    });
  };

  return (
    <div className="taskcards">
      <div className="ml-3 mb-2  d-flex align-items-center">
        <Button
          className="btn-fill p-1"
          size="sm"
          variant="secondary"
          onClick={handleOnClickCardSearch}
        >
          <SearchIcon />
          アーカイブ検索
        </Button>
        <Button
          className="btn-fill p-1 ml-2"
          size="sm"
          variant="info"
          onClick={handleOnClickRecurringManage}
        >
          <AutorenewIcon fontSize="small" />
          定期タスク管理
        </Button>
        {/* 新着 */}
        <div className="ml-auto mr-3">
          <TaskNewNotificationButtonIcon newFlg={newFlg} callEditModal={callEditModal} />
        </div>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        {/* 列用 Droppable（横方向） */}
        <Droppable droppableId="board" direction="horizontal" type="COLUMN">
          {(boardProvided) => (
            <div className="App">
              {/* Droppable と「リストを追加」を分離 */}
              <div className="columns-container">
                <div
                  className="columns-droppable"
                  ref={boardProvided.innerRef}
                  {...boardProvided.droppableProps}
                >
                  {columns.map((column, colIndex) => (
                    // 列をドラッガブルに
                    <Draggable
                      key={column.LIST_ID}
                      draggableId={`col-${column.LIST_ID}`}
                      index={colIndex}
                    >
                      {(colProvided) => (
                        <div
                          className="column"
                          ref={colProvided.innerRef}
                          {...colProvided.draggableProps}
                          style={{
                            background: column.COLOR,
                            width: 250,
                            alignSelf: 'flex-start',
                            ...colProvided.draggableProps.style,
                          }}
                        >
                          {/* 列タイトル + 右上メニュー */}
                          <div
                            className="column-header"
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            {editingListId === column.LIST_ID ? (
                              <Form.Control
                                size="sm"
                                value={editingListName}
                                autoFocus
                                onChange={(e) => setEditingListName(e.target.value)}
                                onBlur={() => saveEditListName(column.LIST_ID)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                    e.preventDefault();
                                    saveEditListName(column.LIST_ID);
                                  }
                                  if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelEditListName();
                                  }
                                }}
                                style={{ flex: 1, marginRight: 4 }}
                              />
                            ) : (
                              <div
                                className="listName"
                                {...colProvided.dragHandleProps}
                                onClick={() => startEditListName(column)}
                                title="クリックしてリスト名を編集"
                                style={{ flex: 1 }}
                              >
                                {column.LIST_NAME}
                              </div>
                            )}
                            <IconButton
                              aria-label="リストメニュー"
                              size="small"
                              onClick={(e) => openListMenu(e, column.LIST_ID)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </div>
                          {/* 列内のカード用 Droppable */}
                          <Droppable droppableId={String(column.LIST_ID)} type="CARD">
                            {(listProvided) => (
                              <div
                                ref={listProvided.innerRef}
                                {...listProvided.droppableProps}
                                className="card-list-droppable"
                                style={{ minHeight: 8 }} // 空でもドロップ可能に
                              >
                                {column.CARDS.map((card, index) => (
                                  <Draggable
                                    key={`card-${card.CARD_ID}`}
                                    draggableId={`card-${card.CARD_ID}`}
                                    index={index}
                                  >
                                    {(cardProvided) => (
                                      <TaskCard
                                        provided={cardProvided}
                                        card={card}
                                        lists={columns}
                                        callEditModal={callEditModal}
                                        updateCompleteCard={updateCompleteCard}
                                      />
                                    )}
                                  </Draggable>
                                ))}
                                {listProvided.placeholder}
                              </div>
                            )}
                          </Droppable>

                          {/* 追加ボタン（リストの下に配置） */}
                          <button
                            type="button"
                            className="add-card"
                            onClick={() => callAddModal(column.LIST_ID)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                callAddModal(column.LIST_ID);
                              }
                            }}
                          >
                            ＋ カードを追加
                          </button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {/* 列並び替え用のプレースホルダは Droppable の最後に置く */}
                  {boardProvided.placeholder}
                </div>

                {/* 追加リストUI（Droppable の外に出す） */}
                {isAddingList ? (
                  <div className="add-list add-list-form">
                    <Form.Control
                      size="sm"
                      placeholder="リスト名を入力"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.nativeEvent.isComposing) confirmAddList();
                        if (e.key === 'Escape') cancelAddList();
                      }}
                    />
                    <div className="actions">
                      <Button
                        size="sm"
                        variant="primary"
                        className="btn-fill"
                        onClick={confirmAddList}
                        disabled={!newListName.trim()}
                      >
                        リストを追加
                      </Button>
                      <Button
                        size="sm"
                        variant="light"
                        className="btn-fill"
                        onClick={cancelAddList}
                      >
                        キャンセル
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="add-list" onClick={startAddList}>
                    <AddIcon className="add-icon" fontSize="small" />
                    リストを追加
                  </button>
                )}
              </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* 追加: リスト用コンテキストメニュー（グローバルに1つ） */}
      <Menu
        anchorEl={listMenuAnchorEl}
        open={isListMenuOpen}
        onClose={closeListMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleMenuAddCard}>カードを追加</MenuItem>
        <MenuItem onClick={handleMenuArchiveList} style={{ color: '#fd2d26ff' }}>
          リストをアーカイブ
        </MenuItem>
      </Menu>
      {/* メッセージダイアログ */}
      <MessageModal dialog={dialog}></MessageModal>
      {/** カード編集モーダル画面 */}
      <TaskCardEditModal params={editModalparam} loadCards={loadCards} />
      {/** 問い合わせ情報モーダル画面 */}
      <TaskCardAddModal params={taskCardAddModalparam} loadCards={loadCards} />
      {/** カード検索モーダル画面 */}
      <TaskArchiveSearchModal
        params={taskArchiveSearchModalparam}
        loadCards={loadCards}
        callEditModal={callEditModal}
      />
      {/** 定期タスク管理モーダル */}
      <RecurringTaskManageModal params={recurringManageParam} callEditModal={callEditModal} loadCards={loadCards} />
    </div>
  );
};

export default TaskCards;
